import { useState, useEffect } from "react";
import { apiCall } from "./api";
import LegalPanel, { LegalLink } from "./legal-panel";
import blackOwlLogo from "./assets/black-owl-logo.png";
import { FONT_BODY, FONT_DISPLAY } from "./theme";

// ============================================================
// BLACK OWL - Landing / Splash Screen
// First screen users hit. Email capture for waitlist +
// early-access code path that jumps straight to login.
// ============================================================

// --- Owl logo image ---
function OwlLogo({ size = 280 }) {
  return (
    <img
      src={blackOwlLogo}
      alt="Black Owl"
      style={{ display: "block", margin: "0 auto", width: "60%", maxWidth: size, height: "auto", objectFit: "contain" }}
    />
  );
}

// --- Live activity feed preview (animated) ---
const FEED_EVENTS = [
  { icon: "🏈", player: "Josh Allen", event: "Passing Touchdown", pts: "+6", color: "#32c850", user: "Marcus" },
  { icon: "📏", player: "Bijan Robinson", event: "50 rushing yards", pts: "+3", color: "#c9a227", user: "Sarah" },
  { icon: "🏹", player: "Sarah", event: "fired 4 arrows at Derek", pts: "-4", color: "#e85555", user: "Sarah" },
  { icon: "⚠", player: "Derek", event: "owes 4 penalties", pts: "", color: "#e85555", user: "Derek" },
  { icon: "🧤", player: "CeeDee Lamb", event: "100 receiving yards", pts: "+6", color: "#32c878", user: "Marcus" },
  { icon: "❌", player: "Jared Goff", event: "Interception", pts: "-2", color: "#e85555", user: "Tina" },
  { icon: "✓", player: "Derek", event: "paid one — 3 left", pts: "", color: "#32c850", user: "Derek" },
  { icon: "🎯", player: "Harrison Butker", event: "Field Goal", pts: "+3", color: "#4a9eff", user: "Tina" },
];

function LiveFeedPreview() {
  const [visible, setVisible] = useState(3);

  useEffect(() => {
    const t = setInterval(() => {
      setVisible((v) => (v >= FEED_EVENTS.length ? 3 : v + 1));
    }, 1800);
    return () => clearInterval(t);
  }, []);

  const shown = FEED_EVENTS.slice(Math.max(0, visible - 4), visible).reverse();

  return (
    <div style={{
      background: "rgba(8, 8, 15, 0.7)",
      border: "1px solid rgba(201, 162, 39, 0.15)",
      borderRadius: "12px",
      padding: "16px 18px",
      marginBottom: "32px",
      overflow: "hidden",
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: "8px",
        marginBottom: "12px",
      }}>
        <span style={{
          width: "7px", height: "7px", borderRadius: "50%",
          background: "#32c850", display: "inline-block",
          animation: "boBlink 1.4s ease-in-out infinite",
        }}/>
        <span style={{
          fontSize: "10px", fontWeight: 700, letterSpacing: "2px",
          color: "#c9a227", fontFamily: FONT_BODY,
        }}>LIVE ACTIVITY</span>
      </div>

      <div style={{ minHeight: "168px" }}>
        {shown.map((e, i) => (
          <div
            key={`${visible}-${i}`}
            style={{
              display: "flex", alignItems: "flex-start", gap: "10px",
              padding: "9px 0",
              borderBottom: i < shown.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none",
              animation: i === 0 ? "boSlideIn 0.45s ease-out" : "none",
              opacity: 1 - i * 0.18,
            }}
          >
            <span style={{ fontSize: "14px", flexShrink: 0, lineHeight: 1.3 }}>{e.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{
                fontSize: "12.5px", margin: 0, lineHeight: 1.4,
                fontFamily: FONT_BODY, color: "#a0a0b0",
              }}>
                <span style={{ fontWeight: 600, color: e.color }}>{e.player}</span>
                {" — "}{e.event}
              </p>
              <p style={{
                fontSize: "9.5px", color: "#3a3a4e", margin: "2px 0 0",
                fontFamily: FONT_BODY,
              }}>{e.user}</p>
            </div>
            {e.pts && (
              <span style={{
                fontSize: "13px", fontWeight: 700, flexShrink: 0,
                color: e.pts.startsWith("-") ? "#e85555" : "#32c850",
                fontFamily: FONT_BODY,
              }}>{e.pts}</span>
            )}
          </div>
        ))}
      </div>

      <style>{`
        @keyframes boSlideIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes boBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation: none !important; }
        }
      `}</style>
    </div>
  );
}

export default function LandingScreen({ onEarlyAccess }) {
  const [email, setEmail] = useState("");
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showLegal, setShowLegal] = useState(null);

  const handleEmailSubmit = async () => {
    if (!email.trim() || !email.includes("@")) {
      return;
    }
    setLoading(true);
    try {
      // Store waitlist email as feedback with a special category
      await apiCall("submit_feedback", {
        username: "waitlist",
        category: "waitlist",
        message: `Waitlist signup: ${email.trim()}`,
        contact: email.trim(),
        screen: "landing",
        timestamp: new Date().toISOString(),
      });
      setEmailSubmitted(true);
    } catch (err) {
      // Even if it fails, show success (don't block the user on a waitlist)
      setEmailSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async () => {
    setCodeError("");
    const code = accessCode.trim().toUpperCase();
    if (!code) {
      setCodeError("Enter your access code.");
      return;
    }
    setLoading(true);
    try {
      const result = await apiCall("verify_access_code", { code });
      if (result.valid) {
        if (onEarlyAccess) onEarlyAccess();
      } else {
        setCodeError("Invalid or expired code.");
      }
    } catch (err) {
      setCodeError("Couldn't verify code. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(180deg, #0a0a14 0%, #0d0d1a 100%)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "20px",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Faint yardline background */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 78px, rgba(201,162,39,0.03) 78px, rgba(201,162,39,0.03) 80px)",
        pointerEvents: "none",
      }}/>

      <div style={{
        position: "relative",
        width: "100%", maxWidth: "440px",
        background: "rgba(12, 12, 22, 0.6)",
        border: "1px solid rgba(201, 162, 39, 0.15)",
        borderRadius: "20px",
        padding: "40px 32px",
        boxShadow: "0 24px 80px rgba(0,0,0,0.5)",
      }}>
        {/* Logo + wordmark */}
        <div style={{ textAlign: "center", marginBottom: "36px" }}>
          <OwlLogo size={280} />
          <h1 style={{
            fontSize: "clamp(26px, 8vw, 38px)", fontWeight: 700, color: "#c9a227",
            margin: "12px 0 6px", letterSpacing: "clamp(3px, 1.5vw, 8px)",
            fontFamily: FONT_DISPLAY, whiteSpace: "nowrap",
          }}>BLACK OWL</h1>
          <p style={{
            fontSize: "clamp(11px, 3vw, 13px)", color: "#5a5a6e", margin: 0,
            letterSpacing: "clamp(3px, 1.2vw, 6px)", fontFamily: FONT_BODY,
            whiteSpace: "nowrap",
          }}>FANTASY DRAFT GAME</p>
        </div>

        {/* Hero headline */}
        <h2 style={{
          fontSize: "clamp(24px, 7vw, 32px)", fontWeight: 700, color: "#e8e6df",
          margin: "0 0 20px", lineHeight: 1.25,
          fontFamily: FONT_DISPLAY,
        }}>
          Fantasy football — except you're all in the same room, and being the loser has <span style={{ color: "#c9a227" }}>consequences.</span>
        </h2>

        {/* Sub copy */}
        <p style={{
          fontSize: "clamp(15px, 4vw, 17px)", color: "#8a8a9e", margin: "0 0 28px",
          lineHeight: 1.6, fontFamily: FONT_BODY,
        }}>
          Draft real NFL players live during the game. Rack up points as they play. Then make your friends do push-ups, dares, and whatever the group cooks up — in real life, in the room.
        </p>

        {/* Live activity feed preview */}
        <LiveFeedPreview />

        {/* Waitlist email capture */}
        {!emailSubmitted ? (
          <>
            <p style={{
              fontSize: "12px", color: "#5a5a6e", margin: "0 0 12px",
              letterSpacing: "3px", fontFamily: FONT_BODY,
            }}>PUBLIC LAUNCH COMING THIS SEASON</p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              style={{
                width: "100%", padding: "16px 18px",
                fontSize: "16px", fontFamily: FONT_BODY,
                color: "#e8e6df", background: "rgba(255,255,255,0.03)",
                border: "1.5px solid rgba(255,255,255,0.1)",
                borderRadius: "10px", outline: "none",
                boxSizing: "border-box", marginBottom: "12px",
              }}
            />
            <button
              onClick={handleEmailSubmit}
              disabled={loading}
              style={{
                width: "100%", padding: "16px",
                fontSize: "14px", fontWeight: 700,
                letterSpacing: "2px", textTransform: "uppercase",
                fontFamily: FONT_BODY,
                color: "#0a0a14",
                background: "linear-gradient(135deg, #c9a227 0%, #e0be4a 50%, #c9a227 100%)",
                border: "none", borderRadius: "10px",
                cursor: loading ? "wait" : "pointer",
                boxShadow: "0 4px 20px rgba(201, 162, 39, 0.25)",
              }}
            >
              {loading ? "..." : "Notify Me When It's Public"}
            </button>
          </>
        ) : (
          <div style={{
            padding: "20px",
            background: "rgba(50, 200, 80, 0.08)",
            border: "1px solid rgba(50, 200, 80, 0.25)",
            borderRadius: "10px",
            textAlign: "center",
          }}>
            <p style={{
              fontSize: "15px", fontWeight: 700, color: "#32c850",
              margin: "0 0 4px", fontFamily: FONT_BODY,
            }}>You're on the list! 🦉</p>
            <p style={{
              fontSize: "13px", color: "#8a8a9e", margin: 0,
              fontFamily: FONT_BODY,
            }}>We'll email you the moment Black Owl opens up.</p>
          </div>
        )}

        {/* Early access code toggle */}
        <div style={{ marginTop: "24px", textAlign: "center" }}>
          {!showCodeInput ? (
            <button
              onClick={() => setShowCodeInput(true)}
              style={{
                background: "none", border: "none",
                fontSize: "12px", color: "#5a5a6e",
                letterSpacing: "1px", fontFamily: FONT_BODY,
                cursor: "pointer", textDecoration: "underline",
                textUnderlineOffset: "3px",
              }}
            >
              Have an early access code?
            </button>
          ) : (
            <div style={{
              padding: "16px",
              background: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(201, 162, 39, 0.15)",
              borderRadius: "10px",
            }}>
              <p style={{
                fontSize: "11px", color: "#c9a227", margin: "0 0 10px",
                letterSpacing: "2px", textTransform: "uppercase",
                fontFamily: FONT_BODY, fontWeight: 700,
              }}>Early Access</p>
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCodeSubmit()}
                  placeholder="ACCESS CODE"
                  style={{
                    flex: 1, padding: "12px 14px",
                    fontSize: "14px", fontFamily: FONT_BODY,
                    color: "#e8e6df", background: "rgba(255,255,255,0.03)",
                    border: "1.5px solid rgba(255,255,255,0.1)",
                    borderRadius: "8px", outline: "none",
                    letterSpacing: "2px", textTransform: "uppercase",
                    boxSizing: "border-box",
                  }}
                />
                <button
                  onClick={handleCodeSubmit}
                  disabled={loading}
                  style={{
                    padding: "12px 20px",
                    fontSize: "12px", fontWeight: 700,
                    letterSpacing: "1px", textTransform: "uppercase",
                    fontFamily: FONT_BODY,
                    color: "#0a0a14",
                    background: "linear-gradient(135deg, #c9a227 0%, #e0be4a 100%)",
                    border: "none", borderRadius: "8px",
                    cursor: loading ? "wait" : "pointer",
                  }}
                >
                  {loading ? "..." : "Enter"}
                </button>
              </div>
              {codeError && (
                <p style={{
                  fontSize: "12px", color: "#e85555", margin: "10px 0 0",
                  fontFamily: FONT_BODY,
                }}>{codeError}</p>
              )}
            </div>
          )}
        </div>

        {/* Footer tagline */}
        <p style={{
          textAlign: "center", marginTop: "28px",
          fontSize: "11px", color: "#3a3a4e",
          letterSpacing: "2px", fontFamily: FONT_BODY,
        }}>
          Draft smart. Play bold. Win big.
        </p>

        {/* Legal footer */}
        <div style={{
          textAlign: "center", marginTop: "16px",
          fontSize: "11px", color: "#3a3a4e",
          fontFamily: FONT_BODY,
        }}>
          <LegalLink label="Terms" onClick={() => setShowLegal("terms")} />
          {"  ·  "}
          <LegalLink label="Privacy" onClick={() => setShowLegal("privacy")} />
          {"  ·  "}
          <LegalLink label="Community" onClick={() => setShowLegal("community")} />
        </div>

        {/* NFL/ESPN disclaimer */}
        <p style={{
          textAlign: "center", marginTop: "12px",
          fontSize: "10px", color: "#2a2a3a",
          lineHeight: 1.5, fontFamily: FONT_BODY,
        }}>
          Not affiliated with or endorsed by the NFL or ESPN.<br/>
          Player data provided by ESPN.
        </p>
      </div>

      {showLegal && (
        <LegalPanel
          initialTab={showLegal}
          onClose={() => setShowLegal(null)}
        />
      )}

      <style>{`
        * { box-sizing: border-box; }
        input::placeholder { color: #3a3a4e; }
      `}</style>
    </div>
  );
}
